import type { 
  OrchestratorConfig, 
  OrchestratorInstance, 
  KaigenticChatGlobalAPI,
  OverlayLayout
} from './types';

import { KaigenticChatOrchestrator } from './orchestrator';
import { waitForDOMReady } from './utils';
import {
  VERSION,
  DEBUG_STORAGE_KEY,
  AUTO_INIT_DELAY
} from './constants';

/**
 * Global API implementation
 */
class KaigenticChatGlobalAPIImpl implements KaigenticChatGlobalAPI {
  private instances = new Map<string, OrchestratorInstance>();
  private primaryInstance: OrchestratorInstance | null = null;
  private globalEventHandlers = new Map<string, Set<Function>>();
  // Per-event bridge handlers wired onto the primary instance. Lazy-installed
  // when a global subscriber appears (so every event the orchestrator emits
  // can reach window.KaigenticChat.on(...) without a hardcoded allowlist),
  // and torn down when the last subscriber for an event unsubscribes.
  private installedBridges = new Map<string, (data: unknown) => void>();
  private autoInitPromise: Promise<OrchestratorInstance> | null = null;

  readonly version = VERSION;

  constructor() {
    // Auto-initialize if configuration is available
    if (typeof window !== 'undefined') {
      this.checkAutoInit();
    }
  }

  /**
   * Initialize with configuration (creates primary instance)
   */
  async init(config: OrchestratorConfig): Promise<OrchestratorInstance> {
    if (this.primaryInstance) {
      console.warn('[KaigenticChat] Primary instance already exists, destroying previous instance');
      this.primaryInstance.destroy();
      // Bridges are closures over the now-destroyed instance. Clear them so
      // the for-loop below re-installs fresh bridges on the new orchestrator
      // — without this, installBridge() early-returns on the stale entries
      // and global subscribers never see events from the new instance.
      this.installedBridges.clear();
    }

    const orchestrator = new KaigenticChatOrchestrator(config);

    // Wire bridges BEFORE init(). 'initialized' fires synchronously at the
    // end of orchestrator.init(), so any pre-init subscriber's bridge must
    // be installed first. Bridges for events with no current subscribers
    // are installed lazily by on() when a subscriber arrives.
    this.primaryInstance = orchestrator;
    this.instances.set('primary', orchestrator);
    for (const event of this.globalEventHandlers.keys()) {
      this.installBridge(event);
    }

    await orchestrator.init();

    return orchestrator;
  }

  /**
   * Create a new instance (non-primary)
   */
  create(config: OrchestratorConfig): OrchestratorInstance {
    const instanceId = `instance_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const orchestrator = new KaigenticChatOrchestrator(config);
    
    this.instances.set(instanceId, orchestrator);

    // Initialize asynchronously
    orchestrator.init().catch(error => {
      console.error('[KaigenticChat] Instance initialization failed:', error);
      this.instances.delete(instanceId);
    });

    return orchestrator;
  }

  /**
   * Destroy all instances
   */
  destroy(): void {
    this.instances.forEach(instance => instance.destroy());
    this.instances.clear();
    this.primaryInstance = null;
    this.installedBridges.clear();
    this.globalEventHandlers.clear();
  }

  /**
   * Install a single bridge handler on the primary instance for `event`,
   * forwarding orchestrator emissions to global subscribers via this.emit.
   * No-op if the bridge is already installed or no primary instance exists.
   */
  private installBridge(event: string): void {
    if (!this.primaryInstance || this.installedBridges.has(event)) return;
    const bridge = (data: unknown) => this.emit(event, data);
    this.installedBridges.set(event, bridge);
    this.primaryInstance.on(event, bridge);
  }

  /**
   * Tear down a previously-installed bridge for `event`. Called when the
   * last global subscriber for the event unsubscribes.
   */
  private uninstallBridge(event: string): void {
    const bridge = this.installedBridges.get(event);
    if (!bridge || !this.primaryInstance) {
      this.installedBridges.delete(event);
      return;
    }
    this.primaryInstance.off(event, bridge);
    this.installedBridges.delete(event);
  }

  // Primary instance control methods
  show(): void {
    if (this.primaryInstance) {
      this.primaryInstance.show();
    } else {
      console.warn('[KaigenticChat] No primary instance available');
    }
  }

  hide(): void {
    if (this.primaryInstance) {
      this.primaryInstance.hide();
    } else {
      console.warn('[KaigenticChat] No primary instance available');
    }
  }

  toggle(): void {
    if (this.primaryInstance) {
      this.primaryInstance.toggle();
    } else {
      console.warn('[KaigenticChat] No primary instance available');
    }
  }

  // Primary instance configuration methods
  updateConfig(config: Partial<OrchestratorConfig>): void {
    if (this.primaryInstance) {
      this.primaryInstance.updateConfig(config);
    } else {
      console.warn('[KaigenticChat] No primary instance available');
    }
  }

  updateLayout(layout: Partial<OverlayLayout>): void {
    if (this.primaryInstance) {
      this.primaryInstance.updateLayout(layout);
    } else {
      console.warn('[KaigenticChat] No primary instance available');
    }
  }

  getConfig(): OrchestratorConfig | null {
    return this.primaryInstance ? this.primaryInstance.getConfig() : null;
  }

  // Primary instance state methods
  isVisible(): boolean {
    return this.primaryInstance ? this.primaryInstance.isVisible() : false;
  }

  isInitialized(): boolean {
    return this.primaryInstance ? this.primaryInstance.isInitialized() : false;
  }

  // Message injection methods (for primary instance)
  injectUserMessage(content: string): void {
    if (this.primaryInstance) {
      this.primaryInstance.injectUserMessage(content);
    } else {
      console.warn('[KaigenticChat] No primary instance available for injection');
    }
  }

  async injectAgentResponse(content: string | object | (string | object)[]): Promise<void> {
    if (this.primaryInstance) {
      return this.primaryInstance.injectAgentResponse(content);
    } else {
      console.warn('[KaigenticChat] No primary instance available for injection');
      return Promise.resolve();
    }
  }

  clearChat(): void {
    if (this.primaryInstance) {
      this.primaryInstance.clearChat();
    } else {
      console.warn('[KaigenticChat] No primary instance available to clear chat');
    }
  }

  // Global event handling. User callbacks live in globalEventHandlers and
  // are fanned out by emit() below. installBridge() wires exactly one
  // forwarder per event-type onto the primary instance, so the user
  // callback is never attached directly to the instance (which would
  // double-fire once the bridge is in place).
  on(event: string, callback: (data: unknown) => void): void {
    if (!this.globalEventHandlers.has(event)) {
      this.globalEventHandlers.set(event, new Set());
    }
    this.globalEventHandlers.get(event)!.add(callback);
    this.installBridge(event);
  }

  off(event: string, callback?: (data: unknown) => void): void {
    const handlers = this.globalEventHandlers.get(event);
    if (!handlers) return;

    if (callback) {
      handlers.delete(callback);
    } else {
      handlers.clear();
    }

    if (handlers.size === 0) {
      this.uninstallBridge(event);
      this.globalEventHandlers.delete(event);
    }
  }

  emit(event: string, data?: unknown): void {
    const handlers = this.globalEventHandlers.get(event);
    if (!handlers || handlers.size === 0) return;

    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`[KaigenticChat] Global event handler error for ${event}:`, error);
      }
    });
  }

  // Utility methods
  getAllInstances(): OrchestratorInstance[] {
    return Array.from(this.instances.values());
  }

  destroyAll(): void {
    this.destroy();
  }

  /**
   * Get debug information
   */
  getDebugInfo(): Record<string, any> {
    return {
      version: this.version,
      instanceCount: this.instances.size,
      hasPrimaryInstance: !!this.primaryInstance,
      globalEventHandlers: Array.from(this.globalEventHandlers.keys()),
      instances: Array.from(this.instances.keys()),
      primaryInstanceState: this.primaryInstance ? {
        initialized: this.primaryInstance.isInitialized(),
        visible: this.primaryInstance.isVisible(),
        config: this.primaryInstance.getConfig().agentName
      } : null
    };
  }

  /**
   * Check for auto-initialization
   */
  private checkAutoInit(): void {
    if (this.autoInitPromise) return;

    const checkConfig = (): boolean => {
      interface WindowWithConfig extends Window {
        kaigenticChatConfig?: Partial<OrchestratorConfig>;
      }
      
      const globalConfig = (window as WindowWithConfig).kaigenticChatConfig;
      if (globalConfig && typeof globalConfig === 'object') {
        if (globalConfig.autoInit !== false) {
          console.log('[KaigenticChat] Auto-initializing with global config');
          this.autoInitPromise = this.init(globalConfig as OrchestratorConfig);
          return true;
        }
      }
      return false;
    };

    // Check immediately
    if (checkConfig()) return;

    // Check when DOM is ready
    waitForDOMReady().then(() => {
      if (!checkConfig() && document.readyState === 'complete') {
        // Final check after full load
        setTimeout(checkConfig, AUTO_INIT_DELAY);
      }
    });
  }

}

// Create global API instance
const globalAPI = new KaigenticChatGlobalAPIImpl();

// Export types and main classes
export * from './types';
export * from './orchestrator';
export * from './utils';

// Export global API as default
export default globalAPI;

// Auto-expose to window if in browser
declare global {
  interface Window {
    KaigenticChat: KaigenticChatGlobalAPI;
    kaigenticChatConfig?: Partial<OrchestratorConfig>;
  }
}

if (typeof window !== 'undefined') {
  window.KaigenticChat = globalAPI;

  // Debug helper
  if (process.env.NODE_ENV === 'development' || localStorage.getItem(DEBUG_STORAGE_KEY)) {
    interface WindowWithDebug extends Window {
      __kaigenticChatDebug?: {
        api: KaigenticChatGlobalAPI;
        version: string;
        getDebugInfo: () => Record<string, unknown>;
      };
    }
    
    (window as WindowWithDebug).__kaigenticChatDebug = {
      api: globalAPI,
      version: VERSION,
      getDebugInfo: () => globalAPI.getDebugInfo()
    };
  }
}

// Log initialization in development
if (process.env.NODE_ENV === 'development') {
  console.log(`[KaigenticChat] JavaScript Orchestrator v${VERSION} loaded`);
}